import { useState, useEffect } from "react";
import { collection, onSnapshot, doc, setDoc, addDoc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { ref } from "firebase/storage";
import BusinessFilingView from "../businesses/BusinessFilingView";
import ClientDetail from "./ClientDetail";
import ClientModal from "./ClientModal";
import Icon from "../ui/Icon";
import StatusProgress from "../ui/StatusProgress";
import { BRAND, THEMES, STAFF, STAFF_COLORS, STATUS_FLOW, STATUS_META } from "../../config/constants";
import { db } from "../../firebase";
import useMobile from "../../hooks/useMobile";
import { msCall } from "../../utils/api";
import { getDefaultTaxYear, formatPhone } from "../../utils/helpers";
import { sp, tp, staffAvatar, btnP } from "../../utils/styles";

export default function ClientsPage({clients,theme,mobile,initialViewClient,initialViewBiz,onClearBiz,staffSigs={},currentUser=""}){
  const t=THEMES[theme];
  const _isMobile=useMobile();
  const isMobile=mobile||_isMobile;
  const [search,setSearch]=useState("");
  const [typeF,setTypeF]=useState("All");
  const [staffF,setStaffF]=useState("All");
  const [statusF,setStatusF]=useState("All");
  const [showAdd,setShowAdd]=useState(false);
  const [editC,setEditC]=useState(null);
  const [viewC,setViewC]=useState(initialViewClient||null);
  const [viewBiz,setViewBiz]=useState(initialViewBiz||null); // {biz, ownerClient} when viewing a business profile
  const [clientBizCounts,setClientBizCounts]=useState({});
  useEffect(()=>{ if(initialViewClient) setViewC(initialViewClient); },[initialViewClient]);
  useEffect(()=>{ if(initialViewBiz){ setViewBiz(initialViewBiz); if(onClearBiz) onClearBiz(); } },[initialViewBiz]);
  // Load all businesses — for counts and flat list
  const [allBusinesses,setAllBusinesses]=useState([]);
  useEffect(()=>{
    const unsub=onSnapshot(collection(db,"businesses"),snap=>{
      const bizList=snap.docs.map(d=>({id:d.id,...d.data()}));
      setAllBusinesses(bizList);
      const counts={};
      bizList.forEach(b=>{if(b.ownerId) counts[b.ownerId]=(counts[b.ownerId]||0)+1;});
      setClientBizCounts(counts);
    });
    return unsub;
  },[]);
  // Flatten: each individual immediately followed by their businesses
  const flatRows=[];
  const clientMap=Object.fromEntries(clients.map(c=>[c.id,c]));
  // Group businesses by ownerId for fast lookup
  const bizByOwner={};
  allBusinesses.forEach(biz=>{
    if(!bizByOwner[biz.ownerId]) bizByOwner[biz.ownerId]=[];
    bizByOwner[biz.ownerId].push(biz);
  });
  clients.forEach(c=>{
    flatRows.push({...c,_rowType:"individual",_ownedBy:null});
    (bizByOwner[c.id]||[]).forEach(biz=>{
      flatRows.push({
        id:c.id,_rowType:"business",
        _bizId:biz.id,_bizName:biz.legalName||"Unnamed Business",
        _bizEntityType:biz.entityType||"LLC",_bizEin:biz.ein,
        _isPrimary:biz.isPrimary,_isBookkeeping:biz.isBookkeeping,
        _ownedBy:c.name,_parentClient:c,
        email:c.email,phone:c.phone,
        assignedTo:c.assignedTo,status:c.status,taxYear:c.taxYear
      });
    });
  });
  const filtered=flatRows.filter(r=>{
    const name=r._rowType==="business"?r._bizName:r.name;
    const ms=name?.toLowerCase().includes(search.toLowerCase())||r.email?.toLowerCase().includes(search.toLowerCase());
    const typeMatch=typeF==="All"||(typeF==="Individual"&&r._rowType==="individual")||(typeF==="Business"&&r._rowType==="business");
    const statusMatch=statusF==="All"||r.status===statusF;
    return ms&&typeMatch&&statusMatch&&(staffF==="All"||r.assignedTo===staffF);
  });
  const saveClient=async(data)=>{
    if(data.id&&clients.find(c=>c.id===data.id)){
      const{id,...rest}=data;
      await updateDoc(doc(db,"clients",id),rest);
      return {id};
    } else {
      const d={...data,createdAt:serverTimestamp()};
      if(!d.id){
        const ty=d.taxYear||getDefaultTaxYear();
        const ref=await addDoc(collection(db,"clients"),{...d,activeYears:[ty],engagementHistory:[]});
        return {id:ref.id};
      } else {
        await setDoc(doc(db,"clients",d.id),d);
        return {id:d.id};
      }
    }
  };
  const updateClient=async(data)=>{const{id,...rest}=data;await updateDoc(doc(db,"clients",id),{...rest,updatedAt:serverTimestamp()});setViewC(data);};
  const deleteClient=async(client,e)=>{
    if(e){e.stopPropagation();}
    const bizNames=(client.businesses||[]).map(b=>b.legalName).filter(Boolean);
    const msg=[
      `Permanently delete ${client.name}?`,
      ``,
      `This will also delete:`,
      `• All invoices for this client`,
      `• OneDrive folder link`,
      `• Uploaded tax return files`,
      bizNames.length ? `• Businesses: ${bizNames.join(", ")}` : null,
      ``,
      `This cannot be undone.`,
    ].filter(l=>l!==null).join("\n");
    if(!window.confirm(msg)) return;

    try {
      // Cloud Function handles cascade delete with admin privileges
      await msCall("deleteClient", { clientId: client.id }, "POST");
    } catch(err) {
      // Fallback: delete client doc directly if function fails
      console.warn("deleteClient function error, falling back:", err.message);
      await deleteDoc(doc(db,"clients",client.id));
    }

    if(viewC?.id===client.id) setViewC(null);
    if(editC?.id===client.id) setEditC(null);
  };
  const bizCount=allBusinesses.length;
  const sc=(label,val,color)=>(<div style={{background:t.surface,border:`1px solid ${color}30`,borderRadius:14,padding:isMobile?"12px 14px":"16px 20px",borderTop:`3px solid ${color}`}}><div style={{fontSize:isMobile?22:30,fontWeight:800,color,letterSpacing:"-1px",marginBottom:2}}>{val}</div><div style={{fontSize:11,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.08em"}}>{label}</div></div>);
  if(showAdd) return <ClientModal onSave={saveClient} onClose={()=>setShowAdd(false)} theme={theme}/>;
  if(editC)   return <ClientModal client={editC} onSave={saveClient} onClose={()=>setEditC(null)} theme={theme}/>;
  if(viewBiz) return <BusinessFilingView biz={viewBiz.biz} ownerClient={viewBiz.ownerClient} onClose={()=>setViewBiz(null)} theme={theme} allClients={clients} currentUser={currentUser}/>;
  if(viewC)   return <ClientDetail client={viewC} onClose={()=>setViewC(null)} onUpdate={updateClient} onDelete={(c)=>deleteClient(c)} theme={theme} staffSigs={staffSigs} allClients={clients} currentUser={currentUser}/>;

  return(
    <div>
      <div style={{display:"grid",gridTemplateColumns:`repeat(${isMobile?2:4},1fr)`,gap:isMobile?10:14,marginBottom:isMobile?14:24}}>
        {sc("Clients",clients.length,BRAND.blue)}{sc("Businesses",bizCount,BRAND.green)}{sc("In Progress",clients.filter(c=>c.status==="Tax Return in Progress").length,"#f59e0b")}{sc("Completed",clients.filter(c=>c.status==="Completed").length,BRAND.green)}
      </div>
      <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:16,padding:isMobile?14:20}}>
        <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",background:t.inputBg,border:`1px solid ${t.border}`,borderRadius:9,padding:"8px 12px",gap:8,flex:1}}>
            <Icon name="search" size={14} color={t.textMuted}/>
            <input style={{background:"transparent",border:"none",outline:"none",color:t.text,fontSize:13,width:"100%",fontFamily:"'Outfit',sans-serif"}} placeholder="Search..." value={search} onChange={e=>setSearch(e.target.value)}/>
          </div>
          <button onClick={()=>setShowAdd(true)} style={{...btnP,whiteSpace:"nowrap"}}><Icon name="plus" size={14} color="#fff"/>{!isMobile&&"Add Client"}</button>
        </div>
        {/* Filter pills — scrollable row on mobile */}
        <div style={{display:"flex",gap:6,marginBottom:6,overflowX:"auto",paddingBottom:2,WebkitOverflowScrolling:"touch"}}>
          {["All","Individual","Business"].map(f=><button key={f} onClick={()=>setTypeF(f)} style={{padding:"6px 12px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${typeF===f?BRAND.blue+"50":t.border}`,background:typeF===f?BRAND.blue+"15":t.bg,color:typeF===f?BRAND.blue:t.textSub,fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",flexShrink:0}}>{f}</button>)}
          <div style={{width:1,background:t.border,margin:"2px 4px",flexShrink:0}}/>
          {["All",...STAFF].map(s=><button key={s} onClick={()=>setStaffF(s)} style={{padding:"6px 11px",borderRadius:8,fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${staffF===s?(STAFF_COLORS[s]||BRAND.blue)+"50":t.border}`,background:staffF===s?(STAFF_COLORS[s]||BRAND.blue)+"15":t.bg,color:staffF===s?(STAFF_COLORS[s]||BRAND.blue):t.textSub,display:"flex",alignItems:"center",gap:5,fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",flexShrink:0}}>
            {s!=="All"&&staffAvatar(s,16)}{s}
          </button>)}
        </div>
        {/* Status filter row */}
        <div style={{display:"flex",gap:6,marginBottom:12,overflowX:"auto",paddingBottom:2,WebkitOverflowScrolling:"touch"}}>
          {["All",...STATUS_FLOW].map(s=>{
            const m=STATUS_META[s];
            const active=statusF===s;
            const count=s==="All"?clients.length:clients.filter(c=>c.status===s).length;
            return <button key={s} onClick={()=>setStatusF(s)} style={{
              padding:"5px 11px",borderRadius:20,fontSize:11,fontWeight:600,cursor:"pointer",
              border:`1px solid ${active?(m?m.border:BRAND.blue)+"80":t.border}`,
              background:active?(m?m.bg(theme):BRAND.blue+"15"):t.bg,
              color:active?(m?m.text:BRAND.blue):t.textMuted,
              fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",flexShrink:0,
              display:"flex",alignItems:"center",gap:5
            }}>
              {s!=="All"&&m&&<span>{m.icon}</span>}
              {s==="All"?"All":s}
              {count>0&&<span style={{fontSize:10,fontWeight:700,background:"rgba(0,0,0,0.1)",borderRadius:10,padding:"0 5px",minWidth:16,textAlign:"center"}}>{count}</span>}
            </button>;
          })}
        </div>

        {/* Mobile: card list / Desktop: table */}
        {isMobile?(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {filtered.filter(r=>r._rowType==="individual").map((r)=>(
              <div key={r.id} onClick={()=>setViewC(r)} style={{background:t.bg,borderRadius:12,padding:"12px 14px",border:`1px solid ${t.border}`,cursor:"pointer",display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:38,height:38,borderRadius:"50%",background:BRAND.blue+"18",border:`2px solid ${BRAND.blue}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:800,color:BRAND.blue,flexShrink:0}}>{(r.name||"?")[0].toUpperCase()}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:13.5,color:t.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.name}</div>
                  <div style={{fontSize:11.5,color:t.textMuted,marginTop:1}}>{r.email}</div>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:4}}>
                    {staffAvatar(r.assignedTo,16)}
                    <span style={sp(r.status,theme)}>{r.status}</span>
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                  <div style={{display:"flex",gap:5}}>
                    <button onClick={e=>{e.stopPropagation();setEditC(r);}} style={{padding:"5px 8px",borderRadius:7,background:"transparent",border:`1px solid ${t.border}`,cursor:"pointer",color:t.textSub}}><Icon name="edit" size={13}/></button>
                    <button onClick={e=>deleteClient(r,e)} style={{padding:"5px 8px",borderRadius:7,background:"transparent",border:"1px solid #f8717130",cursor:"pointer",color:"#f87171"}} title="Delete"><Icon name="trash" size={13} color="#f87171"/></button>
                  </div>
                  {clientBizCounts[r.id]>0&&<span style={{fontSize:10,color:t.textMuted}}>{clientBizCounts[r.id]} biz</span>}
                </div>
              </div>
            ))}
            {filtered.length===0&&<div style={{textAlign:"center",padding:32,color:t.textMuted,fontSize:13}}>No clients match.</div>}
          </div>
        ):(
          <div style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead><tr>{["Name","Type","Contact","Owned By","Assigned","Status","Progress",""].map(h=><th key={h} style={{textAlign:"left",padding:"10px 12px",color:t.textMuted,fontWeight:600,fontSize:10.5,textTransform:"uppercase",letterSpacing:"0.08em",borderBottom:`1px solid ${t.border}`}}>{h}</th>)}</tr></thead>
          <tbody>
            {filtered.map((r,idx)=>{
              const isBiz=r._rowType==="business";
              const displayName=isBiz?r._bizName:r.name;
              const clientToOpen=isBiz?r._parentClient:r;
              return(
                <tr key={`${r.id}-${idx}`} onClick={()=>isBiz?setViewBiz({biz:{id:r._bizId,...allBusinesses.find(b=>b.id===r._bizId)},ownerClient:r._parentClient}):setViewC(clientToOpen)} style={{cursor:"pointer",background:isBiz?(theme==="dark"?"rgba(141,198,63,0.025)":"rgba(141,198,63,0.04)"):"transparent"}}>
                  <td style={{padding:"11px 12px",borderBottom:`1px solid ${t.borderLight}`}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      {isBiz&&<div style={{width:3,height:34,borderRadius:2,background:BRAND.green+"50",flexShrink:0,marginLeft:10}}/>}
                      <div>
                        <div style={{fontWeight:700,fontSize:13,color:isBiz?t.textSub:t.text}}>{displayName}</div>
                        {!isBiz&&r.spouseName&&<div style={{fontSize:11,color:t.textMuted}}>& {r.spouseName}</div>}
                        {isBiz&&r._bizEin&&<div style={{fontSize:10.5,color:t.textMuted}}>EIN: {r._bizEin}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{padding:"11px 12px",borderBottom:`1px solid ${t.borderLight}`}}>
                    {isBiz
                      ?<span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:600,background:theme==="dark"?"#0e2818":"#edfaf0",color:BRAND.green,border:`1px solid ${BRAND.green}30`}}><Icon name="building" size={10} color={BRAND.green}/>{r._bizEntityType}</span>
                      :<span style={tp("Individual",theme)}><Icon name="user" size={10} color={BRAND.blue}/>Individual</span>
                    }
                  </td>
                  <td style={{padding:"11px 12px",borderBottom:`1px solid ${t.borderLight}`}}><div style={{fontSize:12,color:t.textSub}}>{r.email}</div><div style={{fontSize:11,color:t.textMuted}}>{formatPhone(r.phone)}</div></td>
                  <td style={{padding:"11px 12px",borderBottom:`1px solid ${t.borderLight}`}}>
                    {isBiz
                      ?<div style={{display:"flex",alignItems:"center",gap:6}}><div style={{width:6,height:6,borderRadius:"50%",background:BRAND.green,flexShrink:0}}/><span style={{fontSize:12.5,color:t.text,fontWeight:600}}>{r._ownedBy}</span></div>
                      :<span style={{fontSize:11.5,color:t.textMuted}}>—</span>
                    }
                  </td>
                  <td style={{padding:"11px 12px",borderBottom:`1px solid ${t.borderLight}`}}><div style={{display:"flex",alignItems:"center",gap:6}}>{staffAvatar(r.assignedTo,22)}<span style={{fontSize:12,fontWeight:600,color:STAFF_COLORS[r.assignedTo]||t.textSub}}>{r.assignedTo}</span></div></td>
                  <td style={{padding:"11px 12px",borderBottom:`1px solid ${t.borderLight}`}}><span style={sp(r.status,theme)}>{r.status}</span></td>
                  <td style={{padding:"11px 12px",borderBottom:`1px solid ${t.borderLight}`,minWidth:100}}><StatusProgress status={r.status} theme={theme}/></td>
                  <td style={{padding:"11px 12px",borderBottom:`1px solid ${t.borderLight}`}} onClick={e=>e.stopPropagation()}>
                    {!isBiz&&<div style={{display:"flex",gap:5}}>
                      <button onClick={()=>setEditC(r)} style={{padding:"5px 9px",borderRadius:7,background:"transparent",border:`1px solid ${t.border}`,cursor:"pointer",color:t.textSub}}><Icon name="edit" size={13}/></button>
                      <button onClick={e=>deleteClient(r,e)} style={{padding:"5px 9px",borderRadius:7,background:"transparent",border:"1px solid #f8717130",cursor:"pointer",color:"#f87171"}} title="Delete client"><Icon name="trash" size={13} color="#f87171"/></button>
                    </div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length===0&&<div style={{textAlign:"center",padding:40,color:t.textMuted,fontSize:13}}>No clients match your filters.</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// Leads Page
